/**
 * ThankYouPage — dedicated post-payment landing so we can track paid
 * conversions in Google Analytics 4 by region (US vs India).
 *
 * Routing:
 *   /thank-you/us  →  region="us" (USD purchases)
 *   /thank-you/in  →  region="in" (INR purchases)
 *
 * URL query params (all optional, echoed from the payment handler):
 *   plan          one_time | gold
 *   amount        numeric value in currency major units (e.g. 9, 49, 999)
 *   currency      USD | INR
 *   order_id      Razorpay order id (one-time)
 *   payment_id    Razorpay payment id
 *   subscription_id  Razorpay subscription id (gold)
 *   email         guest checkout email
 *   next          audit → send them back to their audit-in-progress after
 *
 * On mount:
 *   1. Fires a **server-side** conversion ping (POST /api/conversion/track)
 *      that stores {region, currency, amount, plan, order_id, payment_id}
 *      in Mongo, deduped by payment_id. Blocking pixels can't hide this.
 *   2. Fires a client-side ``purchase`` GA4 event if gtag is present on
 *      the page (paste the GA4 script tag directly into
 *      /app/frontend/public/index.html — the ANALYTICS SLOT for now).
 *   3. Auto-redirects to /dashboard (or /login for guest checkouts) after
 *      a short delay so we don't strand paid users on a static page.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const REDIRECT_MS = 4000;

function readNumeric(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function ThankYouPage() {
  const { region: regionParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const region = (regionParam || 'us').toLowerCase() === 'in' ? 'in' : 'us';

  const plan = searchParams.get('plan') || '';
  const amount = readNumeric(searchParams.get('amount'));
  const currency = (searchParams.get('currency') || (region === 'in' ? 'INR' : 'USD')).toUpperCase();
  const orderId = searchParams.get('order_id') || '';
  const paymentId = searchParams.get('payment_id') || '';
  const subscriptionId = searchParams.get('subscription_id') || '';
  const email = searchParams.get('email') || '';
  const nextHint = searchParams.get('next') || '';

  // Compute the destination we'll send the user to after the pixel has
  // fired. Guests (no session) don't have a dashboard yet — bounce them
  // to /login with the "you just paid" banner. Logged-in users go
  // straight to /dashboard (or back to their audit if they came from
  // the quota-exceeded flow).
  const nextUrl = useMemo(() => {
    if (email) return `/login?paid=1&email=${encodeURIComponent(email)}`;
    if (nextHint === 'audit') return '/billing/success?next=audit';
    return '/dashboard';
  }, [email, nextHint]);

  const [countdown, setCountdown] = useState(Math.ceil(REDIRECT_MS / 1000));
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;

    // 1) Server-side pixel — never blocked by ad blockers.
    (async () => {
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL || ''}/api/conversion/track`,
          {
            region,
            currency,
            amount,
            plan,
            order_id: orderId || null,
            payment_id: paymentId || null,
            subscription_id: subscriptionId || null,
            email: email || null,
            source: 'thank_you_page',
            page_url: typeof window !== 'undefined' ? window.location.href : null,
          },
          { timeout: 7000 },
        );
      } catch { /* fire and forget */ }
    })();

    // 2) Client-side GA4 event (only fires if gtag is loaded via the
    //    ANALYTICS SLOT in public/index.html). Sends a standard
    //    `purchase` event with a region param so you can build a GA4
    //    audience segment for US vs India buyers.
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', {
          transaction_id: paymentId || orderId || subscriptionId || undefined,
          value: amount || undefined,
          currency: currency || undefined,
          region,
          items: [{ item_id: plan || 'unknown', item_name: plan || 'unknown', price: amount || 0, quantity: 1 }],
        });
      }
    } catch { /* fire and forget */ }
  }, [region, currency, amount, plan, orderId, paymentId, subscriptionId, email]);

  useEffect(() => {
    if (countdown <= 0) {
      navigate(nextUrl, { replace: true });
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, navigate, nextUrl]);

  const regionLabel = region === 'in' ? 'India' : 'United States';

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center px-4"
      data-testid={`thank-you-page-${region}`}
    >
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 text-center shadow-xl">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-500 mb-4">
          <CheckCircle2 size={30} strokeWidth={2.2} aria-hidden="true" />
        </div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          Payment successful
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Redirecting you to your dashboard in
          {' '}
          <span className="font-semibold text-foreground" data-testid="thank-you-countdown">
            {countdown}s
          </span>
          &hellip;
        </p>

        {/* Hidden metadata for GA4 event debuggers & manual QA. */}
        <div className="mt-4 rounded-xl bg-secondary/50 px-3 py-2 text-[11px] font-mono text-muted-foreground">
          <span data-testid="thank-you-region">region: {regionLabel}</span>
          {plan && <> · plan: {plan}</>}
          {amount != null && <> · {currency} {amount}</>}
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <Button
            onClick={() => navigate(nextUrl, { replace: true })}
            className="rounded-xl"
            data-testid="thank-you-continue-button"
          >
            Continue now
          </Button>
          <Link
            to="/dashboard"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="thank-you-dashboard-link"
          >
            Go to dashboard
          </Link>
        </div>

        <div className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 size={12} className="animate-spin" />
          Do not close this tab
        </div>
      </div>
    </div>
  );
}

export default ThankYouPage;
